/*
 * Copyright 2024 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { createTranslationRef } from '@backstage/core-plugin-api/alpha';

/** @public */
export const catalogReactTranslationRef = createTranslationRef({
  id: 'catalog-react',
  messages: {
    catalogFilter: {
      title: 'Filters',
      buttonTitle: 'Filters',
    },
    entityKindPicker: {
      title: 'Kind',
      errorMessage: 'Failed to load entity kinds',
    },
    entityLifecyclePicker: {
      title: 'Lifecycle',
    },
    entityNamespacePicker: {
      title: 'Namespace',
    },
    entityOwnerPicker: {
      title: 'Owner',
    },
    entityProcessingStatusPicker: {
      title: 'Processing Status',
    },
    entityTagPicker: {
      title: 'Tags',
    },
    entityPeekAheadPopover: {
      title: 'Drill into the entity to see all of the tags.',
      emailCardAction: {
        title: 'Email {{email}}',
        subTitle: 'mailto {{email}}',
        ariaLabel: 'Email',
      },
      entityCardActionsAriaLabel: 'Show',
      entityCardActionsTitle: 'Show details',
    },
    entitySearchBar: {
      placeholder: 'Search',
    },
    entityTypePicker: {
      title: 'Type',
      errorMessage: 'Failed to load entity types',
      optionAllTitle: 'all',
    },
    favoriteEntity: {
      addToFavorites: 'Add to favorites',
      removeFromFavorites: 'Remove from favorites',
    },
    inspectEntityDialog: {
      title: 'Entity Inspector',
      closeButtonTitle: 'Close',
      ancestryPage: {
        title: 'Ancestry',
        description:
          'This is the ancestry of entities above the current one - as in, the chain(s) of entities down to the current one, where {{processorsLink}} child entities that ultimately led to the current one existing. Note that this is a completely different mechanism from relations.',
        processorsLink: 'processors emitted',
      },
      colocatedPage: {
        title: 'Colocated',
        description:
          'These are the entities that are colocated with this entity - as in, they originated from the same data source (e.g. came from the same YAML file), or from the same origin (e.g. the originally registered URL).',
        alertNoLocation: 'Entity had no location information.',
        alertNoEntity: 'There were no other entities on this location.',
        locationHeader: 'At the same location',
        originHeader: 'At the same origin',
        entityListAriaLabel: 'Colocated entities',
      },
      jsonPage: {
        title: 'Entity as JSON',
        description:
          'This is the raw entity data as received from the catalog, on JSON form.',
      },
      overviewPage: {
        title: 'Overview',
        relation: {
          title: 'Relations',
        },
        status: {
          title: 'Status',
        },
        identity: {
          title: 'Identity',
        },
        metadata: {
          title: 'Metadata',
        },
        annotations: 'Annotations',
        labels: 'Labels',
        tags: 'Tags',
        copyAriaLabel: 'Copy {{label}}',
        copiedStatus: 'Copied',
        helpLinkAriaLabel: 'Learn more',
      },
      yamlPage: {
        title: 'Entity as YAML',
        description:
          'This is the raw entity data as received from the catalog, on YAML form.',
      },
      tabNames: {
        overview: 'Overview',
        ancestry: 'Ancestry',
        colocated: 'Colocated',
        json: 'Raw JSON',
        yaml: 'Raw YAML',
      },
      tabsAriaLabel: 'Inspector options',
    },
    unregisterEntityDialog: {
      title: 'Are you sure you want to unregister this entity?',
      cancelButtonTitle: 'Cancel',
      deleteButtonTitle: 'Delete Entity',
      deleteEntitySuccessMessage: 'Removed entity {{entityName}}',
      bootstrapState: {
        title:
          'You cannot unregister this entity, since it originates from a protected Backstage configuration (location "{{location}}"). If you believe this is in error, please contact the {{appTitle}} integrator.',
        advancedDescription:
          'You have the option to delete the entity itself from the catalog. Note that this should only be done if you know that the catalog file has been deleted at, or moved from, its origin location. If that is not the case, the entity will reappear shortly as the next refresh round is performed by the catalog.',
        advancedOptions: 'Advanced Options',
      },
      onlyDeleteStateTitle:
        'This entity does not seem to originate from a registered location. You therefore only have the option to delete it outright from the catalog.',
      unregisterState: {
        title: 'This action will unregister the following entities:',
        subTitle: 'Located at the following location:',
        description: 'To undo, just re-register the entity in {{appTitle}}.',
        unregisterButtonTitle: 'Unregister Location',
        advancedOptions: 'Advanced Options',
        advancedDescription:
          'You also have the option to delete the entity itself from the catalog. Note that this should only be done if you know that the catalog file has been deleted at, or moved from, its origin location. If that is not the case, the entity will reappear shortly as the next refresh round is performed by the catalog.',
      },
      errorStateTitle: 'Internal error: Unknown state',
    },
    userListPicker: {
      defaultOrgName: 'Company',
      personalFilter: {
        title: 'Personal',
        ownedLabel: 'Owned',
        starredLabel: 'Starred',
      },
      orgFilterAllLabel: 'All',
    },
    entityTableColumnTitle: {
      name: 'Name',
      system: 'System',
      owner: 'Owner',
      type: 'Type',
      lifecycle: 'Lifecycle',
      namespace: 'Namespace',
      description: 'Description',
      tags: 'Tags',
      targets: 'Targets',
      title: 'Title',
      label: 'Label',
      domain: 'Domain',
    },
    entityRelationCard: {
      emptyHelpLinkTitle: 'Learn how to change this.',
    },
    missingAnnotationEmptyState: {
      title: 'Missing Annotation',
      readMore: 'Read more',
      annotationYaml:
        'Add the annotation to your {{entityKind}} YAML as shown in the highlighted example below:',
      generateDescription_one:
        'The annotation {{annotations}} is missing. You need to add the annotation to your {{entityKind}} if you want to enable this tool.',
      generateDescription_other:
        'The annotations {{annotations}} are missing. You need to add the annotations to your {{entityKind}} if you want to enable this tool.',
    },
  },
});
